import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TICKET_BANDS } from "@/components/pharos/constants";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

export const CONSENT_TEXT =
  "I consent to Elementar sharing my name and email with the sponsor of this deal.";

// Express interest in a published deal: ticket band + explicit consent.
// Elementar reviews the request before anything is shared.
export default function InterestDialog({ deal, open, onOpenChange, onRequested, termsAccepted = true, onTermsRequired }) {
  const [band, setBand] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleClose(isOpen) {
    if (!isOpen) {
      setBand("");
      setConsent(false);
    }
    onOpenChange(isOpen);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!deal || !band || !consent) return;
    if (!termsAccepted) {
      onTermsRequired?.();
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/pharos/listings/${deal.id}/interest`, {
        intended_ticket_band: band,
        consent: true,
      });
      toast.success("Request received");
      handleClose(false);
      onRequested?.(deal);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error("You have already requested this deal.");
        handleClose(false);
        onRequested?.(deal);
      } else if (status === 429) {
        toast.error("Too many requests. Please try again later.");
      } else if (detail === "terms_not_accepted") {
        onTermsRequired?.();
      } else {
        toast.error(getErrorMessage(err, "Failed to send request"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request an introduction</DialogTitle>
          <DialogDescription>
            Elementar reviews every request before anything is shared.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="pi-band" className="text-sm font-medium text-gray-700">
              Intended ticket
            </label>
            <select
              id="pi-band"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={band}
              onChange={(e) => setBand(e.target.value)}
            >
              <option value="">Select...</option>
              {TICKET_BANDS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>{CONSENT_TEXT}</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !band || !consent}
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
            >
              {submitting ? "Sending..." : "Send request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
